import { Controller } from '@hotwired/stimulus';
import { useClickOutside } from 'stimulus-use';

// Older iOS only sends a tap's click to window listeners when an ancestor below
// <body> has a mouse listener (Bootstrap 5 does the same).
const noop = () => {};
const toggleTouchClickable = (on) => {
  if (!('ontouchstart' in document.documentElement)) return;

  const method = on ? 'addEventListener' : 'removeEventListener';
  for (const el of document.body.children) el[method]('mouseover', noop);
};

export default class extends Controller {
  static targets = ['toggle', 'menu'];

  connect() {
    [this.observeOutside, this.unobserveOutside] = useClickOutside(this, {
      events: ['click'],
      onlyVisible: false,
      dispatchEvent: false,
    });
    this.unobserveOutside();
  }

  disconnect() {
    this.close();
  }

  clickOutside() {
    this.close();
  }

  closeOthers = () => this.close();

  toggle(event) {
    event.preventDefault();
    if (this.isOpen) this.close();
    else this.open();
  }

  open() {
    if (this.isOpen) return;

    this.dispatch('open');
    this.observeOutside();
    toggleTouchClickable(true);
    document.addEventListener('dropdown-menu:open', this.closeOthers);
    this.toggleTarget.focus();
    this.toggleTarget.setAttribute('aria-expanded', 'true');
    this.element.classList.add('open');
  }

  close() {
    this.unobserveOutside();
    if (this.isOpen) toggleTouchClickable(false);
    document.removeEventListener('dropdown-menu:open', this.closeOthers);
    this.toggleTarget.setAttribute('aria-expanded', 'false');
    this.element.classList.remove('open');
  }

  keydown(event) {
    if (/input|textarea/i.test(event.target.tagName)) return;

    const onToggle = event.target === this.toggleTarget;

    switch (event.key) {
      case 'Enter':
        // a <div role="button"> toggle has no native Enter activation
        if (!onToggle || /^(a|button)$/i.test(event.target.tagName)) return;
        this.stop(event);
        this.toggle(event);
        return;
      case 'Escape':
        if (!this.isOpen) return;
        this.stop(event);
        this.toggleTarget.focus();
        this.close();
        return;
      case ' ':
        if (!onToggle) return;
      // falls through
      case 'ArrowUp':
      case 'ArrowDown':
        this.stop(event);
        if (this.isOpen) this.focusItem(event);
        else this.open();
        return;
    }
  }

  focusItem(event) {
    const items = [
      ...this.menuTarget.querySelectorAll('a[href], button:not(:disabled)'),
    ].filter(
      (item) =>
        !item.closest('li.disabled') && item.getClientRects().length > 0,
    );
    if (!items.length) return;

    let index = items.indexOf(event.target);
    if (event.key === 'ArrowUp' && index > 0) index--;
    if (event.key === 'ArrowDown' && index < items.length - 1) index++;
    items[Math.max(index, 0)].focus();
  }

  stop(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  get isOpen() {
    return this.element.classList.contains('open');
  }
}
